"use client";

import { useId } from "react";
import { cn } from "@/src/lib/utils";

import type { CursorFollowBadgeProps } from "./CursorFollowBadge.types";

/**
 * A small circular label that spins continuously and follows the cursor
 * position exposed by its parent via the `--cursor-x`/`--cursor-y`/
 * `--cursor-visible` CSS custom properties (see `CursorTrackArea`, which
 * also keeps these correct across scroll). Uses `mix-blend-mode: difference`
 * so its color inverts against whatever is underneath — it stays readable
 * over any image without needing to know the image's colors ahead of time.
 *
 * Purely presentational and `pointer-events-none`, so it never intercepts
 * clicks.
 */
export function CursorFollowBadge({ label, size = 96, className }: CursorFollowBadgeProps) {
  const pathId = useId();
  const repeatedLabel = `${label}   •   `.repeat(2);
  const radius = size / 2 - 10;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      className={cn(
        "pointer-events-none absolute top-0 left-0 flex items-center justify-center mix-blend-difference",
        className,
      )}
      style={{
        width: size,
        height: size,
        opacity: "var(--cursor-visible, 0)",
        transitionProperty: "opacity",
        transitionDuration: "var(--cursor-transition-duration, 300ms)",
        transitionTimingFunction: "ease",
        transform:
          "translate(calc(var(--cursor-x, 50%) - 50%), calc(var(--cursor-y, 50%) - 50%))",
      }}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        className="animate-spin-slow"
      >
        <path
          id={pathId}
          fill="none"
          d={`M ${size / 2}, ${size / 2} m -${radius}, 0 a ${radius},${radius} 0 1,1 ${radius * 2},0 a ${radius},${radius} 0 1,1 -${radius * 2},0`}
        />
        <text
          fill="#ffffff"
          fontSize="11"
          fontWeight="300"
          xmlSpace="preserve"
          className="uppercase"
        >
          <textPath
            href={`#${pathId}`}
            startOffset="0%"
            textLength={circumference}
            lengthAdjust="spacing"
          >
            {repeatedLabel}
          </textPath>
        </text>
      </svg>
    </div>
  );
}